'use client';

/**
 * Small pieces every Camps page shares: names, times, user text, flairs, the
 * upvote, the translate button, menus and dialogs.
 *
 * USER TEXT IS NEVER HTML. `RichText` splits plain text into strings and
 * React elements (links, mentions); React escapes every string, and no
 * component in components/camps uses dangerouslySetInnerHTML.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { fetchLanguages, translateStream, type Language } from '@/lib/api';
import { loadTabs } from '@/lib/languagePrefs';
import type { Flair, Person, Awards, AwardKind, VoteDir } from '@/lib/campsApi';
import { signInHref, AWARD_LIST, awardTarget } from '@/lib/campsApi';
import Layer from '../Layer';
import { StatusChip } from '../StatusEmblem';
import type { ChipStatus } from '@/lib/status';

// ── Styles ───────────────────────────────────────────────────────────────────

export const CARD = 'rounded-2xl border border-ink-200 bg-white dark:border-ink-800 dark:bg-ink-900';
export const BTN = 'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full px-4 text-[13.5px] font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60';
export const BTN_PRIMARY = BTN + ' bg-accent-600 text-white hover:bg-accent-700 dark:bg-accent-400 dark:text-ink-950 dark:hover:bg-accent-300';
export const BTN_QUIET = BTN + ' border border-ink-200 text-ink-700 hover:bg-ink-100 dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800';
export const BTN_GHOST = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-semibold text-ink-600 transition hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60 disabled:opacity-50';
export const INPUT = 'w-full rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-[14px] text-ink-900 placeholder:text-ink-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/25 dark:border-ink-700 dark:bg-ink-950 dark:text-ink-50';

export const fmtCount = (n: number) => new Intl.NumberFormat('en', { notation: n >= 10000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(n || 0);

export function timeAgo(iso: string | Date): string {
  const t = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  if (s < 365 * 86400) return `${Math.floor(s / (30 * 86400))}mo ago`;
  return `${Math.floor(s / (365 * 86400))}y ago`;
}
export function TimeAgo({ iso, prefix = '' }: { iso: string; prefix?: string }) {
  const [, tick] = useState(0);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 60_000); return () => clearInterval(i); }, []);
  return <time dateTime={iso} title={new Date(iso).toLocaleString()} suppressHydrationWarning>{prefix}{timeAgo(iso)}</time>;
}

// ── People ───────────────────────────────────────────────────────────────────

/** Only the name and the picture are read, so any `{ name, avatar }` will do (team, developers, mod log). */
export function Avatar({ person, size = 20 }: { person: (Pick<Person, 'name' | 'avatar'> & Partial<Person>) | null; size?: number }) {
  const [bad, setBad] = useState(false);
  // Reused for somebody else (a list re-sorted, a profile navigated): their picture gets its own try.
  useEffect(() => setBad(false), [person?.avatar]);
  const name = person?.name ?? '?';
  if (person?.avatar && !bad) {
    // eslint-disable-next-line @next/next/no-img-element -- a small, already-sized avatar
    return <img src={person.avatar} alt="" width={size} height={size} onError={() => setBad(true)} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  return (
    <span aria-hidden className="grid shrink-0 place-items-center rounded-full font-bold uppercase text-white"
          style={{ width: size, height: size, fontSize: size * 0.48, background: `hsl(${hue} 55% 48%)` }}>
      {person ? name.replace(/^demo_/, '').slice(0, 1) : '–'}
    </span>
  );
}

/** Rank chip colours by band: penalties (1–7), Stranger (8), then rising. */
export function rankTone(level: number): string {
  if (level <= 7) return 'border-rose-300/70 bg-rose-50 text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200';
  if (level === 8) return 'border-ink-200 bg-ink-50 text-ink-500 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-400';
  if (level <= 11) return 'border-sky-300/70 bg-sky-50 text-sky-700 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-200';
  if (level <= 14) return 'border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200';
  if (level <= 17) return 'border-violet-300/70 bg-violet-50 text-violet-700 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-200';
  return 'border-amber-400/70 bg-amber-50 text-amber-800 dark:border-amber-300/40 dark:bg-amber-300/10 dark:text-amber-200';
}

/** The public reputation rank (backend lib/reputation.ts): 20 ranks, Dead … Stranger … Legend. */
export function RankChip({ level, rank, className = '' }: { level: number; rank: string; className?: string }) {
  return (
    <span title={`Rank ${level} of 20: ${rank}`}
          className={'shrink-0 rounded-[5px] border px-1 text-[9.5px] font-bold uppercase leading-[15px] tracking-wide ' + rankTone(level) + ' ' + className}>
      {rank}
    </span>
  );
}

/** "u/name", linking to their profile, with their status (or rank), and a small DEMO badge on the seeded starter accounts. */
export function UserName({ person, className = '', noRank = false }: { person: Person | null; className?: string; noRank?: boolean }) {
  if (!person) return <span className={'text-ink-500 dark:text-ink-400 ' + className}>[deleted]</span>;
  return (
    <span className={'relative z-[1] inline-flex min-w-0 items-center gap-1 ' + className}>
      <Link href={`/u/${encodeURIComponent(person.name)}`} title={`${(person.kharma ?? 0).toLocaleString('en-US')} KHARMA earned from upvotes`}
            className="truncate font-semibold text-ink-800 hover:underline dark:text-ink-100">u/{person.name}</Link>
      {/* While the Reputation mode is live the server sends the status of the 50 (lib/status.ts); else today's rank. */}
      {!noRank && !person.demo && !person.system && ((person as Person & { status?: ChipStatus }).status
        ? <StatusChip status={(person as Person & { status: ChipStatus }).status} />
        : person.rank && <RankChip level={person.level} rank={person.rank} />)}
      {person.system && (
        <span title="The TrueMarch team's own account" className="shrink-0 rounded-[5px] border border-accent-300/70 bg-accent-50 px-1 text-[9.5px] font-bold uppercase leading-[15px] tracking-wide text-accent-700 dark:border-accent-400/30 dark:bg-accent-400/10 dark:text-accent-200">truemarch</span>
      )}
      {person.demo && (
        <span title="Demo account — starter content written by the TrueMarch team, not a real person"
              className="shrink-0 rounded-[5px] border border-amber-400/60 bg-amber-50 px-1 text-[9.5px] font-bold uppercase leading-[15px] tracking-wide text-amber-700 dark:border-amber-300/40 dark:bg-amber-300/10 dark:text-amber-200">
          demo
        </span>
      )}
    </span>
  );
}

// ── Flairs ───────────────────────────────────────────────────────────────────

export const FLAIR_TONES: Record<string, string> = {
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200',
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200',
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-400/15 dark:text-violet-200',
  rose: 'bg-rose-100 text-rose-800 dark:bg-rose-400/15 dark:text-rose-200',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-400/15 dark:text-slate-200',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-400/15 dark:text-orange-200',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-400/15 dark:text-teal-200',
};
export const FLAIR_COLORS = Object.keys(FLAIR_TONES);
/** Solid swatches for the flair colour picker (the chip tones are too faint to choose by in dark mode). */
export const FLAIR_SWATCH: Record<string, string> = {
  amber: '#f59e0b', emerald: '#10b981', sky: '#0ea5e9', violet: '#8b5cf6', rose: '#f43f5e', slate: '#64748b', orange: '#f97316', teal: '#14b8a6',
};
export function FlairChip({ flair, onClick, active }: { flair: Pick<Flair, 'text' | 'color'>; onClick?: () => void; active?: boolean }) {
  const cls = 'inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ' + (FLAIR_TONES[flair.color] ?? FLAIR_TONES.slate) +
    (active ? ' ring-2 ring-accent-500' : '');
  return onClick
    ? <button type="button" onClick={onClick} className={cls + ' min-h-[28px] hover:brightness-95'}><span className="truncate">{flair.text}</span></button>
    : <span className={cls}><span className="truncate">{flair.text}</span></span>;
}

// ── User text ────────────────────────────────────────────────────────────────

const TOKEN = /(https?:\/\/[^\s<>"']+)|(?<![\w/])(u\/[A-Za-z0-9_-]{2,40})|(?<![\w/])(c\/[A-Za-z0-9_]{3,21})\b/g;

/** The bold marks a writer may put in a post (owner 2026-11-01: "users can make a bold to some letters"): `**…**` on one line. */
export const BOLD_RE = /\*\*([^*\n]+?)\*\*/g;
/** Plain text with its `**bold**` runs as <strong> (never HTML); `key` keeps the keys unique where it is spliced in. */
export function boldParts(text: string, key: string | number, cls = 'font-bold'): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0, m: RegExpExecArray | null, k = 0;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<strong key={`${key}b${k++}`} className={cls}>{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
/**
 * The composers' **B** button (owner 2026-11-01): wraps the textarea's selection in `**…**` (again: unwraps it), keeps the
 * letters selected; nothing selected → "**bold**" with the word selected to type over. Ctrl/⌘-B in the box does the same
 * (boldKey). The post shows those letters bold (RichText, the world's post view, TrueMarch TV).
 */
export function BoldButton({ target, value, onChange, className = '', max = 20000 }: {
  target: React.RefObject<HTMLTextAreaElement | null>; value: string; onChange(v: string): void; className?: string; max?: number;
}) {
  return (
    <button type="button" title="Bold (Ctrl+B): select letters, then press — the post shows them bold" aria-label="Bold"
            onMouseDown={(e) => e.preventDefault()} onClick={() => applyBold(target.current, value, onChange, max)}
            className={'inline-grid h-7 min-w-7 place-items-center rounded-md border px-1.5 font-serif text-[14px] font-extrabold ' + className}>B</button>
  );
}
function applyBold(ta: HTMLTextAreaElement | null, value: string, onChange: (v: string) => void, max: number): void {
  const a = ta?.selectionStart ?? value.length, b = ta?.selectionEnd ?? value.length;
  const r = boldSelection(value, a, b);
  if (r.value.length > max) return;
  onChange(r.value);
  requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(r.start, r.end); } });
}
/** Ctrl/⌘-B in a composer's box: bold (onKeyDown). */
export function boldKey(e: React.KeyboardEvent<HTMLTextAreaElement>, value: string, onChange: (v: string) => void, max = 20000): void {
  if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') { e.preventDefault(); applyBold(e.currentTarget, value, onChange, max); }
}
/** Wrap the selection of a textarea in `**…**` (the composers' B button): the new value and where the selection goes. */
export function boldSelection(value: string, start: number, end: number): { value: string; start: number; end: number } {
  const sel = value.slice(start, end);
  if (sel.startsWith('**') && sel.endsWith('**') && sel.length > 4) return { value: value.slice(0, start) + sel.slice(2, -2) + value.slice(end), start, end: end - 4 };
  if (start >= 2 && value.slice(start - 2, start) === '**' && value.slice(end, end + 2) === '**') return { value: value.slice(0, start - 2) + sel + value.slice(end + 2), start: start - 2, end: end - 2 };
  const word = sel || 'bold';
  return { value: value.slice(0, start) + `**${word}**` + value.slice(end), start: start + 2, end: start + 2 + word.length };
}

/** Plain text → paragraphs with auto-linked URLs, `c/name` links and `u/name` mentions. Never HTML. */
export function RichText({ text, className = '' }: { text: string; className?: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  let k = 0;
  while ((m = TOKEN.exec(text))) {
    let token = m[0];
    // Trailing punctuation belongs to the sentence, not the link.
    if (m[1]) { const trim = token.match(/[).,;:!?\]]+$/); if (trim) token = token.slice(0, -trim[0].length); }
    if (m.index > last) parts.push(...boldParts(text.slice(last, m.index), `t${k++}`));
    if (m[1]) {
      parts.push(<a key={k++} href={token} target="_blank" rel="nofollow ugc noopener noreferrer" className="break-words text-accent-600 underline decoration-accent-600/30 underline-offset-2 hover:decoration-accent-600 dark:text-accent-400">{token}</a>);
    } else if (m[2]) {
      parts.push(<Link key={k++} href={`/u/${encodeURIComponent(token.slice(2))}`} className="font-semibold text-accent-700 hover:underline dark:text-accent-300">{token}</Link>);
    } else {
      parts.push(<Link key={k++} href={`/${token.toLowerCase()}`} className="font-semibold text-accent-700 hover:underline dark:text-accent-300">{token}</Link>);
    }
    last = m.index + token.length;
    TOKEN.lastIndex = last;
  }
  if (last < text.length) parts.push(...boldParts(text.slice(last), `t${k++}`));
  return <div className={'whitespace-pre-wrap break-words [overflow-wrap:anywhere] ' + className}>{parts}</div>;
}

// ── Votes ────────────────────────────────────────────────────────────────────

/**
 * Up- and downvote, Reddit style: one vote per person, pressing the lit arrow
 * takes it back, pressing the other switches it. The number is the score
 * (upvotes − downvotes). Optimistic: it moves at once and rolls back if the
 * server says no.
 */
export function Votes({ vote, score, onVote, disabled, disabledTitle, compact = false }: {
  vote: VoteDir; score: number; onVote: (dir: VoteDir) => Promise<{ vote: VoteDir; score: number }>;
  disabled?: boolean; disabledTitle?: string; compact?: boolean;
}) {
  const [state, setState] = useState({ vote, score });
  const [err, setErr] = useState('');
  const busy = useRef(false);
  useEffect(() => { setState({ vote, score }); }, [vote, score]);
  const press = async (dir: 1 | -1) => {
    if (disabled || busy.current) return;
    busy.current = true;
    const prev = state;
    const want: VoteDir = prev.vote === dir ? 0 : dir;
    setState({ vote: want, score: prev.score - prev.vote + want }); setErr('');
    try { const r = await onVote(want); setState({ vote: r.vote, score: r.score }); }
    catch (e) { setState(prev); setErr((e as Error).message); setTimeout(() => setErr(''), 4000); }
    busy.current = false;
  };
  const tone = state.vote === 1 ? 'text-orange-600 dark:text-orange-300' : state.vote === -1 ? 'text-indigo-600 dark:text-indigo-300' : 'text-ink-700 dark:text-ink-200';
  const arrow = (dir: 1 | -1) => {
    const on = state.vote === dir;
    const label = dir === 1 ? (on ? 'Remove your upvote' : 'Upvote') : (on ? 'Remove your downvote' : 'Downvote');
    return (
      <button type="button" onClick={() => press(dir)} aria-pressed={on} disabled={disabled} aria-label={label}
              title={disabled ? disabledTitle : label}
              className={'inline-flex items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60 disabled:cursor-not-allowed ' +
                (compact ? 'h-8 w-8 ' : 'h-9 w-9 ') +
                (on ? (dir === 1 ? 'text-orange-600 dark:text-orange-300' : 'text-indigo-600 dark:text-indigo-300')
                  : 'text-ink-500 hover:bg-ink-100 hover:text-ink-800 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100')}>
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden>
          <path d={dir === 1 ? 'M12 4l7.5 8.5H15V20H9v-7.5H4.5z' : 'M12 20l7.5-8.5H15V4H9v7.5H4.5z'} />
        </svg>
      </button>
    );
  };
  return (
    <span className={'relative inline-flex items-center rounded-full ' + (state.vote === 1 ? 'bg-orange-500/10 dark:bg-orange-400/10' : state.vote === -1 ? 'bg-indigo-500/10 dark:bg-indigo-400/10' : '') + (disabled ? ' opacity-70' : '')}>
      {arrow(1)}
      <span className={'min-w-[1.5ch] text-center text-[13px] font-bold tabular-nums ' + tone}>
        {state.score < 0 ? '−' + fmtCount(-state.score) : fmtCount(state.score)}<span className="sr-only"> points</span>
      </span>
      {arrow(-1)}
      {err && <span role="alert" className="absolute left-0 top-full z-20 mt-1 w-56 rounded-lg bg-ink-900 px-2.5 py-1.5 text-[12px] text-white shadow-lg dark:bg-ink-100 dark:text-ink-900">{err}</span>}
    </span>
  );
}

// ── Translate ────────────────────────────────────────────────────────────────

const LANG_KEY = 'kh_camps_lang';
let langCache: Promise<Language[]> | null = null;
const languages = () => (langCache ??= fetchLanguages().catch(() => { langCache = null; return []; }));

function readerLanguage(list: Language[]): string {
  try { const v = localStorage.getItem(LANG_KEY); if (v && list.some((l) => l.code === v)) return v; } catch { /* private mode */ }
  const codes = list.map((l) => l.code);
  return loadTabs('target', codes)[0] ?? 'en';
}

/** One answer per text + language for the visit: a language picked back asks nothing. */
const translated = new Map<string, string>();

/**
 * Translate a post or comment into the reader's language, in place.
 * The same /api/translate call the translator page makes, so it costs KHARMA
 * exactly as a normal translation does — also when someone else already
 * translated it and the server answers from its cache (owner 2026-10-21); free
 * only when this reader already paid for it (`owned`). Signed out → a sign-in
 * link instead.
 */
export function useTranslation(text: string, signedIn: boolean) {
  const [state, setState] = useState<{
    status: 'idle' | 'busy' | 'done' | 'error'; out: string; error: string; lang: string; showing: boolean;
    /** What it cost (null: nothing said), and whether it was already theirs. */
    kharma: number | null; owned: boolean;
  }>({ status: 'idle', out: '', error: '', lang: '', showing: false, kharma: null, owned: false });
  const [langs, setLangs] = useState<Language[]>([]);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  const run = async (lang?: string) => {
    if (!signedIn) { window.location.assign(signInHref()); return; }
    const list = langs.length ? langs : await languages();
    if (!langs.length) setLangs(list);
    const to = lang ?? readerLanguage(list);
    abort.current?.abort();
    const mk = `${to}\u0000${text}`;
    const kept = translated.get(mk);
    if (kept !== undefined) { setState({ status: 'done', out: kept, error: '', lang: to, showing: true, kharma: null, owned: true }); return; }
    const ctl = new AbortController();
    abort.current = ctl;
    let acc = '';
    setState({ status: 'busy', out: '', error: '', lang: to, showing: true, kharma: null, owned: false });
    await translateStream({ text: text.slice(0, 4800), from: 'auto', to, mode: 'translate', signal: ctl.signal }, {
      onDelta: (c) => { acc += c; setState((s) => ({ ...s, out: acc })); },
      onDone: (d) => {
        const out = d.text || acc;
        if (out.trim()) translated.set(mk, out);
        setState((s) => ({ ...s, status: 'done', out, kharma: Number.isFinite(d.kharma) ? Number(d.kharma) : null, owned: !!d.owned }));
      },
      onError: (m) => setState((s) => ({ ...s, status: 'error', error: m || 'Translation failed.', showing: !!acc })),
      onLimit: (m) => setState((s) => ({ ...s, status: 'error', error: m, showing: !!acc })),
    });
  };
  const choose = (code: string) => { try { localStorage.setItem(LANG_KEY, code); } catch { /* private mode */ } void run(code); };
  return { ...state, langs, run, choose, toggle: () => setState((s) => ({ ...s, showing: !s.showing })), truncated: text.length > 4800 };
}

export function TranslateIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5h9M8.5 3v2M6 5c.6 3 2.6 5.6 5.5 7M11 5c-.8 3.6-3.2 6.4-7 8" />
      <path d="M12.5 21l4-10 4 10M14 17.5h5" />
    </svg>
  );
}

/** The row under translated text: what it is, and the way back. */
export function TranslateBar({ t }: { t: ReturnType<typeof useTranslation> }) {
  if (t.status === 'idle') return null;
  const name = t.langs.find((l) => l.code === t.lang)?.name ?? t.lang;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-500 dark:text-ink-400">
      {t.status === 'busy' && <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500" />Translating into {name}…</span>}
      {t.status === 'done' && t.showing && (
        <span>Translated into {name}{t.truncated ? ' (first 4,800 characters)' : ''}
          {t.owned ? ' · already yours, free' : t.kharma ? ` · ${t.kharma.toLocaleString()} KHARMA` : ''}</span>
      )}
      {t.status === 'error' && <span role="alert" className="text-rose-600 dark:text-rose-300">{t.error}</span>}
      {(t.status === 'done' || (t.status === 'error' && t.out)) && (
        <button type="button" onClick={t.toggle} className="min-h-[32px] font-semibold text-accent-700 hover:underline dark:text-accent-300">
          {t.showing ? 'Show original' : 'Show translation'}
        </button>
      )}
      {t.langs.length > 0 && t.status !== 'busy' && (
        <label className="inline-flex items-center gap-1">
          <span className="sr-only">Translate into</span>
          <select value={t.lang} onChange={(e) => t.choose(e.target.value)}
                  className="min-h-[32px] max-w-[9rem] rounded-full border border-ink-200 bg-transparent px-2 text-[12px] dark:border-ink-700">
            {t.langs.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

export function TranslateButton({ t, signedIn }: { t: ReturnType<typeof useTranslation>; signedIn: boolean }) {
  return (
    <button type="button" onClick={() => (t.status === 'done' && !t.showing ? t.toggle() : void t.run(t.lang || undefined))}
            disabled={t.status === 'busy'}
            title={signedIn ? 'Translate into your language (uses KHARMA like any translation)' : 'Sign in to translate'}
            className={BTN_GHOST + ' text-accent-700 dark:text-accent-300'}>
      <TranslateIcon />
      {t.status === 'busy' ? 'Translating…' : 'Translate'}
    </button>
  );
}

// ── Menus and dialogs ────────────────────────────────────────────────────────

export function Menu({ items, label = 'More' }: { items: ({ label: string; onClick: () => void; danger?: boolean } | false | null | undefined)[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [open]);
  const list = items.filter(Boolean) as { label: string; onClick: () => void; danger?: boolean }[];
  if (!list.length) return null;
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={BTN_GHOST + ' min-w-[36px] justify-center'}>
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-40 mt-1 min-w-[11rem] overflow-hidden rounded-xl border border-ink-200 bg-white p-1 shadow-xl dark:border-ink-700 dark:bg-ink-900">
          {list.map((it) => (
            <button key={it.label} type="button" role="menuitem" onClick={() => { setOpen(false); it.onClick(); }}
                    className={'flex min-h-[40px] w-full items-center rounded-lg px-3 text-left text-[13px] font-medium hover:bg-ink-100 dark:hover:bg-ink-800 ' +
                      (it.danger ? 'text-rose-600 dark:text-rose-300' : 'text-ink-700 dark:text-ink-200')}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Dialog({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', key); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <Layer>
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title}
           className={'max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl dark:bg-ink-900 sm:rounded-3xl ' + (wide ? 'sm:max-w-2xl' : 'sm:max-w-md')}>
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-ink-100 bg-white/95 px-5 py-3.5 backdrop-blur dark:border-ink-800 dark:bg-ink-900/95">
          <h2 className="text-[16px] font-bold text-ink-900 dark:text-ink-50">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
    </Layer>
  );
}

const REPORT_REASONS = ['Spam or scam', 'Harassment or hate', 'Breaks this Camp\'s rules', 'Misinformation', 'Sexual or violent content', 'Personal information'];

export function ReportDialog({ what, onSend, onClose }: { what: string; onSend: (reason: string) => Promise<unknown>; onClose: () => void }) {
  const [pick, setPick] = useState('');
  const [more, setMore] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState(false);
  const send = async () => {
    setBusy(true); setMsg('');
    try { await onSend([pick, more.trim()].filter(Boolean).join(': ').slice(0, 300)); setDone(true); } catch (e) { setMsg((e as Error).message); }
    setBusy(false);
  };
  return (
    <Dialog title={`Report ${what}`} onClose={onClose}>
      {done ? (
        <div className="py-2">
          <p className="text-[14px] text-ink-800 dark:text-ink-100">Thanks. The moderators and the TrueMarch team will look at it.</p>
          <button type="button" onClick={onClose} className={BTN_PRIMARY + ' mt-4 w-full'}>Done</button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {REPORT_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setPick(r)} aria-pressed={pick === r}
                      className={'min-h-[40px] rounded-full border px-3.5 text-[13px] font-medium transition ' +
                        (pick === r ? 'border-accent-600 bg-accent-50 text-accent-700 dark:border-accent-400 dark:bg-accent-400/10 dark:text-accent-300' : 'border-ink-200 text-ink-700 hover:bg-ink-100 dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800')}>
                {r}
              </button>
            ))}
          </div>
          <textarea value={more} onChange={(e) => setMore(e.target.value)} maxLength={200} rows={3} placeholder="Anything the moderators should know (optional)" className={INPUT + ' mt-3'} />
          {msg && <p role="alert" className="mt-2 text-[13px] text-rose-600 dark:text-rose-300">{msg}</p>}
          <button type="button" disabled={!pick || busy} onClick={send} className={BTN_PRIMARY + ' mt-4 w-full'}>{busy ? 'Sending…' : 'Send report'}</button>
        </>
      )}
    </Dialog>
  );
}

/** A reason prompt for moderator removals and bans. */
export function ReasonDialog({ title, action, onSubmit, onClose, required = false }: { title: string; action: string; onSubmit: (reason: string) => Promise<unknown>; onClose: () => void; required?: boolean }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  return (
    <Dialog title={title} onClose={onClose}>
      <label className="block text-[13px] font-semibold text-ink-700 dark:text-ink-200" htmlFor="reason-box">Reason {required ? '' : '(optional, shown to the author)'}</label>
      <textarea id="reason-box" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} className={INPUT + ' mt-1.5'} />
      {msg && <p role="alert" className="mt-2 text-[13px] text-rose-600 dark:text-rose-300">{msg}</p>}
      <button type="button" disabled={busy || (required && !reason.trim())} className={BTN_PRIMARY + ' mt-4 w-full'}
              onClick={async () => { setBusy(true); setMsg(''); try { await onSubmit(reason.trim()); onClose(); } catch (e) { setMsg((e as Error).message); setBusy(false); } }}>
        {busy ? 'Working…' : action}
      </button>
    </Dialog>
  );
}

export function Empty({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <div className={CARD + ' flex flex-col items-center px-6 py-12 text-center'}>
      {icon}
      <p className="mt-3 text-[15px] font-semibold text-ink-800 dark:text-ink-100">{title}</p>
      {children && <div className="mt-1.5 max-w-sm text-[13.5px] leading-relaxed text-ink-500 dark:text-ink-400">{children}</div>}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden className={'animate-pulse rounded-xl bg-ink-100 dark:bg-ink-800 ' + className} />;
}

export function TentIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className + ' shrink-0'} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 4L3 20h18L12 4z" />
      <path d="M12 11l-3.5 9M12 11l3.5 9" />
      <path d="M12 4V2.5M12 2.5l2.5 1" />
    </svg>
  );
}
export function LockIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className + ' shrink-0'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}

// ── Awards ───────────────────────────────────────────────────────────────────

/** The three awards' marks: a heart (Thanks), a star (Great), a crown (Legendary). Flat, two colours. */
export function AwardIcon({ kind, className = 'h-4 w-4' }: { kind: AwardKind; className?: string }) {
  if (kind === 'thanks') return (
    <svg viewBox="0 0 24 24" className={className + ' shrink-0'} aria-hidden><path fill="#f43f5e" d="M12 20.5l-1.3-1.2C5.4 14.6 2 11.5 2 7.7 2 4.6 4.4 2.3 7.4 2.3c1.7 0 3.4.8 4.6 2.1 1.2-1.3 2.9-2.1 4.6-2.1 3 0 5.4 2.3 5.4 5.4 0 3.8-3.4 6.9-8.7 11.6z" /></svg>
  );
  if (kind === 'great') return (
    <svg viewBox="0 0 24 24" className={className + ' shrink-0'} aria-hidden><path fill="#f59e0b" stroke="#b45309" strokeWidth="1" strokeLinejoin="round" d="M12 2.5l2.9 6 6.6.8-4.9 4.5 1.3 6.5L12 17.1l-5.9 3.2 1.3-6.5L2.5 9.3l6.6-.8z" /></svg>
  );
  return (
    <svg viewBox="0 0 24 24" className={className + ' shrink-0'} aria-hidden><path fill="#facc15" stroke="#a16207" strokeWidth="1" strokeLinejoin="round" d="M3 8l4.5 4L12 4l4.5 8L21 8l-2 11H5z" /><rect x="5" y="19" width="14" height="2" fill="#a16207" /></svg>
  );
}

/** The awards a post or comment has received, as small marks with counts. */
export function AwardsRow({ awards, className = '' }: { awards: Awards | undefined; className?: string }) {
  const list = AWARD_LIST.filter((a) => (awards?.[a.id] ?? 0) > 0);
  if (!list.length) return null;
  return (
    <span className={'inline-flex items-center gap-2 text-[12px] font-semibold text-ink-600 dark:text-ink-300 ' + className}>
      {list.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-0.5" title={`${a.label} award${(awards?.[a.id] ?? 0) > 1 ? ` ×${awards?.[a.id]}` : ''}`}>
          <AwardIcon kind={a.id} className="h-3.5 w-3.5" />{(awards?.[a.id] ?? 0) > 1 && <span className="tabular-nums">{awards?.[a.id]}</span>}
        </span>
      ))}
    </span>
  );
}

/**
 * "Award": pay KHARMA to thank the author. They keep 50–85% of it depending
 * on the KHARMA they have earned from upvotes (Visitor, Contributor, Top contributor).
 */
export function AwardButton({ slug, type, id, signedIn, disabled, onAwarded }: {
  slug: string; type: 'post' | 'comment'; id: string; signedIn: boolean; disabled?: boolean; onAwarded: (awards: Awards) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<AwardKind | null>(null);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState('');
  if (disabled) return null;
  const give = async (kind: AwardKind) => {
    setBusy(kind); setMsg('');
    try {
      const r = await awardTarget(slug, type, id, kind);
      onAwarded(r.awards);
      setDone(`Sent. The author received ${r.paid.toLocaleString('en-US')} KHARMA. You have ${r.balance.toLocaleString('en-US')} left.`);
    } catch (e) { setMsg((e as Error).message); }
    setBusy(null);
  };
  return (
    <>
      <button type="button" className={BTN_GHOST} title="Give an award"
              onClick={() => (signedIn ? (setOpen(true), setDone(''), setMsg('')) : window.location.assign(signInHref()))}>
        <AwardIcon kind="great" className="h-4 w-4 opacity-80" /><span className="hidden sm:inline">Award</span>
      </button>
      {open && (
        <Dialog title={`Award this ${type}`} onClose={() => setOpen(false)}>
          <p className="text-[13px] leading-relaxed text-ink-600 dark:text-ink-300">
            Pay KHARMA to thank the author. They keep half of it, or more as the KHARMA they have
            earned from upvotes grows (70% from 100, 85% from 5,000).
          </p>
          <div className="mt-3 grid gap-2">
            {AWARD_LIST.map((a) => (
              <button key={a.id} type="button" disabled={!!busy || !!done} onClick={() => give(a.id)}
                      className="flex min-h-[56px] items-center gap-3 rounded-2xl border border-ink-200 px-3.5 text-left transition hover:border-accent-400 hover:bg-accent-50/50 disabled:opacity-50 dark:border-ink-700 dark:hover:bg-ink-800">
                <AwardIcon kind={a.id} className="h-7 w-7" />
                <span className="min-w-0 flex-1 text-[14px] font-bold text-ink-900 dark:text-ink-50">{a.label}</span>
                <span className="shrink-0 text-[13px] font-bold tabular-nums text-ink-800 dark:text-ink-100">{busy === a.id ? '…' : `${a.price.toLocaleString('en-US')} KHARMA`}</span>
              </button>
            ))}
          </div>
          {msg && <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700 dark:bg-rose-400/10 dark:text-rose-200">{msg}</p>}
          {done && <p role="status" className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800 dark:bg-emerald-400/10 dark:text-emerald-200">{done}</p>}
        </Dialog>
      )}
    </>
  );
}

/** CQS tier chip for moderators (private; never shown to the person themself). */
export function CqsChip({ tier }: { tier: string | null | undefined }) {
  if (!tier) return null;
  const tone = tier === 'lowest' ? 'bg-rose-100 text-rose-800 dark:bg-rose-400/15 dark:text-rose-200'
    : tier === 'low' ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200'
    : tier === 'moderate' ? 'bg-slate-100 text-slate-700 dark:bg-slate-400/15 dark:text-slate-200'
    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200';
  return <span title="Contributor quality (private: only moderators see this)" className={'rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ' + tone}>CQS {tier}</span>;
}
